import ClassNames from 'classnames'
import * as React from 'react'
import styled from 'styled-components'
import { getInquiryLink } from 'utils/locale'
import I18n from '../../core/i18n'
import * as constants from '../../static/constants'
import { Button } from 'components/atoms'
import Burger from './Burger'
import { IUser } from 'static/interfaces'
import Typography from './_layouts/Typography'
import { IconName } from '../../types/icon-names'
import { agentService } from '../../core/services'
import SvgIcon from 'components/atoms/SvgIcon'
import { useBodyClassForMediaQuery } from 'utils/hooks'
import { clearReservationStorageAndInfo } from 'utils/reservationSessionStorage'

interface IWindow {
  localePath: { [key: string]: string }
  location: any
  flashMessages: any
  globalModal: any
}

declare var window: IWindow

interface IProps {
  user: any
  agentMember?: { name: string; email: string }
  isSignedIn: boolean
  desktop?: boolean
  mobile?: boolean
  lang?: { [key: string]: string }
  top?: boolean
  fromDashboardHeader?: boolean
}

interface IMenuListProps {
  user: any
  agentMember?: { name: string; email: string }
  inquiryLink: string
  isSignedIn?: boolean
  desktop?: boolean
  mobile?: boolean
  setIsOpen?: (isOpen: boolean) => void
}
type MenuType = 'language' | 'favorite' | 'user'
interface IToggleIconMenuProps {
  user: IUser
  agentMember?: { name: string; email: string }
  isOpenedType: MenuType | null
  setIsOpenType: React.Dispatch<React.SetStateAction<MenuType | null>>
  type: MenuType
}
const getCsrfToken = (): string =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''

const signOut = async (isAgentMember: boolean) => {
  const path = isAgentMember ? '/login/agent/sign_out' : '/users/sign_out'
  try {
    await fetch(path, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': getCsrfToken(),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
  } catch (e) {
    // ネットワークエラー等でもセッションをクリアしてリダイレクトする
    console.error('サインアウトリクエスト失敗:', e)
  }
  clearReservationStorageAndInfo()
  // サーバー側のセッション破棄完了を待ってからリダイレクトする
  setTimeout(() => {
    window.location.href = isAgentMember ? '/login/agent' : '/'
  }, 300)
}

const ChangePasswordForm: React.FC = () => {
  const [currentPassword, setCurrentPassword] = React.useState('')
  const [newPassword, setNewPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')

  const handleSubmit = React.useCallback(async () => {
    if (newPassword.length < 12) {
      window.flashMessages.addMessage({ text: I18n.t('generic.password_too_short'), type: 'error' })
      return
    }
    if (newPassword !== confirmPassword) {
      window.flashMessages.addMessage({ text: I18n.t('generic.password_mismatch'), type: 'error' })
      return
    }
    try {
      const { flush } = await agentService.updateAgentMemberPassword({
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword,
      })
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      window.globalModal.closeModal()
    } catch (e) {
      // httpClientインターセプターでエラー表示済み
    }
  }, [currentPassword, newPassword, confirmPassword])

  return (
    <PasswordFormWrapper>
      <div className="field">
        <label>{I18n.t('generic.current_password')}</label>
        <input
          type="password"
          value={currentPassword}
          onChange={e => setCurrentPassword(e.target.value)}
        />
      </div>
      <div className="field">
        <label>{I18n.t('generic.new_password')}</label>
        <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
      </div>
      <div className="field">
        <label>{I18n.t('generic.password_confirm')}</label>
        <input
          type="password"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
        />
      </div>
      <Button primary={true} handleClick={handleSubmit}>
        {I18n.t('generic.update')}
      </Button>
    </PasswordFormWrapper>
  )
}

const showChangePasswordModal = () => {
  window.globalModal.showModal({
    title: I18n.t('generic.change_password'),
    body: <ChangePasswordForm />,
    closeText: I18n.t('generic.close'),
  })
}

const changeLocaleLink = (locale: string) => {
  const urlParams = new URLSearchParams(window.location.search)
  urlParams.set('locale', locale)
  document.location.href = `${window.location.pathname}?${urlParams.toString()}`
}

const ToggleIconMenuButton: React.FC<IToggleIconMenuProps> = ({
  user,
  agentMember,
  type,
  isOpenedType,
  setIsOpenType,
}) => {
  const isOpen = type === isOpenedType
  const menuRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isOpen) {
      return
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpenType(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, setIsOpenType])

  return (
    <ToggleIconMenu ref={menuRef}>
      <div
        className={ClassNames(['iconButton', { active: isOpen, hasUser: !!user }])}
        onClick={() => setIsOpenType(old => (old === type ? null : type))}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsOpenType(old => (old === type ? null : type))
          }
          if (e.key === 'Escape') {
            setIsOpenType(null)
          }
        }}
        role="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={type}
        tabIndex={0}
      >
        {type === 'language' && (
          <SvgIcon name="language" className="icon" ariaHidden={true} role="button" />
        )}
        {type === 'favorite' && (
          <SvgIcon
            name="favorite_border"
            className="icon"
            ariaHidden={true}
            role="button"
            onClick={
              user
                ? () => (window.location.href = '/favorites')
                : () => (window.location.href = '/users/sign_in')
            }
          />
        )}
        {type === 'user' &&
          (user && !agentMember ? (
            <img src={user.avatar_url} width={26} height={26} />
          ) : (
            <SvgIcon name="account_circle" className="icon" ariaHidden={true} role="button" />
          ))}
      </div>
      {type !== 'favorite' && isOpen && (
        <div
          className={`ToggleIconMenu${type === 'user' ? ' user' : ''}`}
          tabIndex={-1}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setIsOpenType(null)
            }
          }}
        >
          {type === 'language' && (
            <div className="ToggleIconMenu_Item" onClick={() => changeLocaleLink('en')}>
              English
            </div>
          )}
          {type === 'language' && (
            <div className="ToggleIconMenu_Item" onClick={() => changeLocaleLink('ja')}>
              日本語
            </div>
          )}
          {type === 'user' &&
            (user ? (
              <>
                <UserSection>
                  <div className="LoginUser">
                    {!agentMember && (
                      <img className="userIcon" src={user.avatar_url} width={32} height={32} />
                    )}
                    <div>
                      <div className="greeting">Hello,</div>
                      <div className="userName">
                        {agentMember ? agentMember.name : user.fullname}
                      </div>
                      {agentMember && <div className="parentAgent">{user.fullname}</div>}
                    </div>
                  </div>
                </UserSection>
                {user.agent && (
                  <>
                    <a className="ToggleIconMenu_Item" href="/agent/dashboard">
                      {I18n.t('top_nav.dashboard')}
                    </a>
                    <a className="ToggleIconMenu_Item" href="/agent/agent_members">
                      {I18n.t('top_nav.agent')}
                    </a>
                  </>
                )}
                {user.guide && (
                  <>
                    <a className="ToggleIconMenu_Item" href="/agent/dashboard">
                      {I18n.t('top_nav.dashboard')}
                    </a>
                    <a className="ToggleIconMenu_Item" href="/conversations">
                      {I18n.t('top_nav.conversations')}
                    </a>
                  </>
                )}
                {user.admin && (
                  <>
                    <a className="ToggleIconMenu_Item" href="/admin/posts">
                      {I18n.t('top_nav.admin')}
                    </a>
                  </>
                )}
                {user.guest && (
                  <>
                    <a className="ToggleIconMenu_Item" href="/reservations">
                      {I18n.t('top_nav.reservations')}
                    </a>
                    <a className="ToggleIconMenu_Item" href="/conversations">
                      {I18n.t('top_nav.conversations')}
                    </a>
                  </>
                )}
                {!agentMember && (
                  <a className="ToggleIconMenu_Item" href="/settings">
                    {I18n.t('top_nav.settings')}
                  </a>
                )}
                {agentMember && (
                  <a
                    href="#"
                    className="ToggleIconMenu_Item"
                    onClick={e => {
                      e.preventDefault()
                      showChangePasswordModal()
                    }}
                  >
                    {I18n.t('generic.change_password')}
                  </a>
                )}
                <a
                  href="#"
                  className="ToggleIconMenu_Item"
                  rel="nofollow"
                  onClick={e => {
                    e.preventDefault()
                    signOut(!!agentMember)
                  }}
                >
                  {I18n.t('top_nav.logout')}
                </a>
                {!user.admin && !agentMember && (
                  <a
                    className="ToggleIconMenu_Item border"
                    href={getInquiryLink()}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Contact Us
                  </a>
                )}
              </>
            ) : (
              <>
                <a className="ToggleIconMenu_Item" href="/users/sign_in">
                  Login
                </a>

                <div className="ToggleIconMenu_Item">
                  <Button handleClick={() => (window.location.href = '/users/sign_up')}>
                    Sign up
                  </Button>
                </div>
                <a
                  className="ToggleIconMenu_Item border"
                  href={getInquiryLink()}
                  target="_blank"
                  rel="noreferrer"
                >
                  Contact Us
                </a>
              </>
            ))}
        </div>
      )}
    </ToggleIconMenu>
  )
}
const UserSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  font-size: 14px;
  .userIcon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
  }

  .greeting {
    font-weight: 400;
    font-size: 12px;
    color: ${constants.COLORS.subText};
  }

  .parentAgent {
    font-size: 11px;
    color: ${constants.COLORS.subText};
    margin-top: 2px;
  }

  &.mobile {
    flex-direction: column;
    gap: 20px;
    .userName {
      font-size: 18px;
    }
    &.signin {
      flex-direction: row;
      align-items: center;
      .userName {
        font-weight: 700;
      }
      .userIcon {
        width: 76px;
        height: 76px;
      }
    }
  }

  .LoginUser,
  .NoLoginUser {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .LoginUser {
    padding: 0 24px;
  }
  .SigninButtons {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    .SignupButton,
    .LoginButton {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 46px;
      border-radius: 4px;
      cursor: pointer;
    }

    .mobile {
      padding: 24px;
      .LoginUser {
        flex-direction: column;
      }
    }
    .SignupButton {
      border: 1px solid ${constants.COLORS.primary};
      color: ${constants.COLORS.primary};
    }
    .LoginButton {
      border: 1px solid ${constants.COLORS.divider2};
    }
  }

  .Setting_Link {
    font-size: 16px;
    margin-top: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 5px 16px;

    background: #f7f7f7;
    border-radius: 17px;
    color: ${constants.COLORS.primary};
  }
`
const IconMenus = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  .forAgencies {
    font-size: 15px;
    color: ${constants.COLORS.primary};
    text-decoration: underline;
    margin-right: 26px;
    &:hover {
      text-decoration: none;
    }
  }
`

const ToggleIconMenu = styled.div`
  position: relative;
  .iconButton {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 32px;
    border-radius: 30px;
    transition: background-color 0.2s;
    &:hover,
    &.active {
      background-color: ${constants.COLORS.successBG2};
    }
    &.hasUser {
      background-color: transparent;
      &:hover,
      &.active {
        > img {
          border: 2px solid ${constants.COLORS.primary};
        }
      }
    }
    .icon {
      width: 24px;
      height: 24px;
    }
    > img {
      width: 26px;
      height: 26px;
      border-radius: 50%;
    }
  }

  .ToggleIconMenu {
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background: ${constants.COLORS.white};
    box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    gap: 20px;
    top: 48px;
    left: 50%;
    z-index: 1000;
    white-space: nowrap;
    padding: 20px 0;
    &.user {
      left: unset;
      right: 0;
    }
    &:not(.user) {
      transform: translateX(-50%);
    }

    .ToggleIconMenu_Item {
      cursor: pointer;
      width: 100%;
      color: ${constants.COLORS.black};
      padding: 0 24px;
      &:hover {
        color: ${constants.COLORS.primary};
      }
      &.border {
        padding-top: 16px;
        border-top: 1px solid ${constants.COLORS.background2};
      }
    }
  }
`

interface IMobileMenuItemProps {
  icon: IconName
  text: string
  href: string
  newTab?: boolean
  isSignOut?: boolean
}
const MobileMenuItem = ({ icon, text, href, newTab, isSignOut }: IMobileMenuItemProps) => {
  const rel = newTab ? 'noopener noreferrer' : isSignOut ? 'nofollow' : ''
  const linkOptions = {
    href,
    rel,
    target: newTab ? '_blank' : '_self',
    'data-method': isSignOut ? 'delete' : null,
  }
  return (
    <a className="MobileMenu_Item" {...linkOptions}>
      <div className="Item_Label">
        <SvgIcon name={icon} className="icon" ariaHidden={true} role="button" />
        <Typography type="body" size="xl">
          {text}
        </Typography>
      </div>
      <SvgIcon name="chevron_right" className="icon" ariaHidden={true} role="button" />
    </a>
  )
}

const MenuList: React.VFC<IMenuListProps> = props => (
  <ul
    className={ClassNames('Profile_Menu', {
      desktop: props.desktop,
      mobile: props.mobile,
    })}
  >
    {props.isSignedIn ? (
      <>
        {props.user.agent && (
          <>
            <li>
              <MobileMenuItem
                icon="note"
                text={I18n.t('top_nav.dashboard')}
                href="/agent/dashboard"
              />
            </li>
            <li>
              <MobileMenuItem
                icon="person"
                text={I18n.t('top_nav.agent')}
                href="/agent/agent_members"
              />
            </li>
            <li>
              <MobileMenuItem
                icon="chat"
                text={I18n.t('top_nav.conversations')}
                href="/conversations"
              />
            </li>
          </>
        )}
        {props.user.guide && (
          <>
            <MobileMenuItem
              icon="note"
              text={I18n.t('top_nav.dashboard')}
              href="/agent/dashboard"
            />

            <MobileMenuItem
              icon="chat"
              text={I18n.t('top_nav.conversations')}
              href="/conversations"
            />
            <MobileMenuItem icon="mail" text="Contact Us" newTab={true} href={props.inquiryLink} />
          </>
        )}
        {props.user.admin && (
          <>
            <MobileMenuItem icon="tour" text={I18n.t('top_nav.admin')} href="/admin/posts" />
          </>
        )}
        {props.user.guest && (
          <>
            <li>
              <MobileMenuItem
                icon="calender"
                text={I18n.t('top_nav.reservations')}
                href="/reservations"
              />
            </li>
            <li>
              <MobileMenuItem
                icon="chat"
                text={I18n.t('top_nav.conversations')}
                href="/conversations"
              />
            </li>
            <li>
              <MobileMenuItem
                icon="favorite_border"
                text={I18n.t('top_nav.favorites')}
                href="/favorites"
              />
            </li>
            <li>
              <MobileMenuItem
                icon="mail"
                text="Contact Us"
                newTab={true}
                href={props.inquiryLink}
              />
            </li>
          </>
        )}
        {!props.agentMember && (
          <li className="separate">
            <MobileMenuItem icon="settings" text={I18n.t('top_nav.settings')} href="/settings" />
          </li>
        )}
        {props.agentMember && (
          <li className="separate">
            <a
              href="#"
              className="MobileMenu_Item"
              onClick={e => {
                e.preventDefault()
                showChangePasswordModal()
              }}
            >
              <div className="Item_Label">
                <SvgIcon name="settings" className="icon" ariaHidden={true} role="button" />
                <Typography type="body" size="xl">
                  {I18n.t('generic.change_password')}
                </Typography>
              </div>
              <SvgIcon name="chevron_right" className="icon" ariaHidden={true} role="button" />
            </a>
          </li>
        )}
        <li className="separate">
          <a
            href="#"
            className="MobileMenu_Item"
            rel="nofollow"
            onClick={e => {
              e.preventDefault()
              signOut(!!props.agentMember)
            }}
          >
            <div className="Item_Label">
              <SvgIcon name="logout" className="icon" ariaHidden={true} role="button" />
              {I18n.t('top_nav.logout')}
            </div>
            <SvgIcon name="chevron_right" className="icon" ariaHidden={true} role="button" />
          </a>
        </li>
      </>
    ) : (
      <>
        <MobileMenuItem
          icon="favorite_border"
          text={I18n.t('top_nav.favorites')}
          href="/users/sign_in"
        />
        <MobileMenuItem icon="person" text="Contact Us" newTab={true} href={props.inquiryLink} />
      </>
    )}
  </ul>
)

const HeaderMenu: React.FC<IProps> = props => {
  const [isOpenedType, setIsOpenType] = React.useState<MenuType | null>(null)
  const [isOpen, setIsOpen] = React.useState(false)
  const { isSignedIn, user, agentMember } = props

  const inquiryLink = React.useMemo(() => {
    return getInquiryLink()
  }, [])

  const selectRef = React.useRef(null)
  const onClickParent = () => {
    if (selectRef.current) {
      selectRef.current.focus()
      selectRef.current.showPicker()
    }
  }

  useBodyClassForMediaQuery(
    constants.HAS_FIXED_FOOTER_CLASS,
    `(max-width: ${constants.BREAKPOINT_DESKTOP - 1}px)`,
    Boolean(props.mobile && isOpen)
  )

  if (props.mobile) {
    return (
      <>
        <Burger isOpen={isOpen} setIsOpen={setIsOpen} />

        {isOpen && (
          <MobileMenu className="MobileMenu" top={props.top}>
            {/* メニューヘッダー */}
            <div className="MobileMenu_Header">
              <p className="MobileMenu_Header_Title">Menu</p>
              <div className="Close_MenuList" onClick={() => setIsOpen(false)}>
                <SvgIcon name="close" className="icon" ariaHidden={true} role="button" />
              </div>
            </div>
            {/* ユーザー */}
            <div className="MobileMenu_User">
              <UserSection
                className={ClassNames({
                  signin: !!user,
                  mobile: true,
                })}
              >
                {props.isSignedIn ? (
                  <>
                    <img className="userIcon" src={user.avatar_url} />
                    <div>
                      <div className="greeting">Hello,</div>
                      <div className="userName">
                        {agentMember ? agentMember.name : user.fullname}
                      </div>
                      {agentMember && <div className="parentAgent">{user.fullname}</div>}
                      {!agentMember && (
                        <a className="Setting_Link" href="/settings">
                          Settings
                        </a>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="NoLoginUser">
                      <img
                        className="userIcon"
                        src={`${constants.ASSET_HOST}/images/no-avatar.svg`}
                        alt="No user"
                      />
                      <div className="userName">Guest</div>
                    </div>
                    <div className="SigninButtons">
                      <div
                        className="LoginButton"
                        role="button"
                        onClick={() => (window.location.href = '/users/sign_in')}
                      >
                        Log in
                      </div>
                      <div
                        className="SignupButton"
                        role="button"
                        onClick={() => (window.location.href = '/users/sign_up')}
                      >
                        Sign up
                      </div>
                    </div>
                  </>
                )}
              </UserSection>
            </div>
            <div className="MobileMenu_Scroller">
              {/* 各メニュー */}
              <MenuList
                user={user}
                agentMember={props.agentMember}
                mobile={true}
                isSignedIn={isSignedIn}
                inquiryLink={inquiryLink}
              />
              {/* フッター */}
              <div className="MobileMenu_Footer">
                <a
                  className="forAgencies"
                  href={constants.DMC_LINK}
                  target="_blank"
                  rel="noreferrer"
                >
                  For Agencies
                </a>
                <div className="LocaleSelect" onClick={onClickParent}>
                  <SvgIcon name="language" className="icon" ariaHidden={true} role="button" />
                  <select
                    onChange={e => changeLocaleLink(e.target.value)}
                    value={I18n.locale}
                    ref={selectRef}
                  >
                    <option value="en">English</option>
                    <option value="ja">日本語</option>
                  </select>
                  <SvgIcon
                    name="keyboard_arrow_down"
                    className="icon"
                    ariaHidden={true}
                    role="button"
                  />
                </div>
              </div>
            </div>
          </MobileMenu>
        )}
      </>
    )
  }
  if (props.desktop) {
    return (
      <IconMenus>
        <a className="forAgencies" href={constants.DMC_LINK} target="_blank" rel="noreferrer">
          For Agencies
        </a>
        <ToggleIconMenuButton
          user={props.user}
          type="language"
          isOpenedType={isOpenedType}
          setIsOpenType={setIsOpenType}
        />
        <ToggleIconMenuButton
          user={props.user}
          agentMember={props.agentMember}
          type="user"
          isOpenedType={isOpenedType}
          setIsOpenType={setIsOpenType}
        />
      </IconMenus>
    )
  }

  return null
}

const MobileMenu = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  z-index: 8000;
  background-color: ${constants.COLORS.white};
  box-shadow: 0 8px 16px rgba(10, 10, 10, 0.1);
  color: ${constants.COLORS.black};
  display: flex;
  flex-direction: column;
  height: 100%;

  .MobileMenu_Header,
  .MobileMenu_User {
    padding: 0 42px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      padding: 0 16px;
    }
  }

  .MobileMenu_Header_Title {
    font-size: 22px;
    font-weight: 700;
  }

  .MobileMenu_Header {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    height: ${constants.MOBILE_HEADER_HEIGHT}px;

    .Close_MenuList {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      cursor: pointer;
      border-radius: 16px;
      border: 1px solid ${constants.COLORS.divider2};
      .icon {
        width: 24px;
        height: 24px;
      }
    }
  }
  .MobileMenu_User {
    border-bottom: 1px solid ${constants.COLORS.background2};
    padding-bottom: 30px;
  }
  .MobileMenu_Scroller {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow-y: auto;
    overscroll-behavior: contain;
  }
  .Profile_Menu {
    display: inline-flex;
    flex-direction: column;
  }
  .MobileMenu_Item {
    color: ${constants.COLORS.black};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 24px 42px;
    border-bottom: 1px solid ${constants.COLORS.background2};
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      padding: 24px 16px;
    }
    .Item_Label {
      display: inline-flex;
      flex-direction: row;
      gap: 12px;
    }
  }
  .MobileMenu_Footer {
    margin-top: auto;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 16px;
    .LocaleSelect {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      select {
        appearance: none;
        margin: 0;
        padding: 0;
        background: none transparent;
        vertical-align: middle;
        font-size: inherit;
        color: inherit;
        outline: none;
        border: none;
      }
    }
    .forAgencies {
      font-size: 15px;
      color: ${constants.COLORS.primary};
      text-decoration: underline;
      margin-right: 26px;
    }
  }
`

const PasswordFormWrapper = styled.div`
  .field {
    margin-bottom: 16px;
    label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      margin-bottom: 4px;
      color: ${constants.COLORS.subText};
    }
    input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid ${constants.COLORS.divider};
      border-radius: 4px;
      font-size: 14px;
    }
  }
  .Button {
    width: 100%;
    margin-top: 8px;
  }
`

export default HeaderMenu
